// =====================================================
// AdminSalesExecutives  (Sales Executive Module — Tasks 1, 7 & 8)
//
// The admin-side Sales Executive Management page. One component covers the
// whole admin surface of the module:
//   • Overview counters               (Task 7)
//   • Create Sales Executive          (Task 1 — name + email only)
//   • Executives table + controls     (Task 8 — suspend / activate / remove)
//   • Incentive review queue          (Task 8 — approve / reject / mark paid)
//   • Referral statistics drill-down  (Task 8)
//   • Fraud reports                   (Task 8)
//
// It is rendered in two places, exactly like the existing AdSubmit /
// AdsAnalytics components: as a section inside the Admin Dashboard, and as its
// own page at /sales-admin. `embedded` drops the page chrome (root wrapper +
// Navbar) so the Admin Dashboard does not end up with two navbars.
//
// Every endpoint it calls is admin-only on the server — the UI never decides
// authorisation.
// =====================================================

import { useCallback, useEffect, useState } from "react";
import Navbar from "./Navbar";
import { salesAdmin, errorMessage } from "../services/salesApi";
import AdminSecurityPanel from "./AdminSecurityPanel";
import "./AdminSalesExecutives.css";

const rupees = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function fmtDay(day) {
  if (!day) return "—";
  const d = new Date(`${day}T00:00:00`);
  if (isNaN(d)) return day;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export default function AdminSalesExecutives({ embedded = false }) {
  const [overview, setOverview] = useState(null);
  const [executives, setExecutives] = useState([]);
  const [incentives, setIncentives] = useState([]);
  const [fraud, setFraud] = useState([]);

  const [incentiveFilter, setIncentiveFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Create form — the admin enters ONLY name + email; the account must exist.
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [creating, setCreating] = useState(false);
  const [formMsg, setFormMsg] = useState(null); // { type: "ok" | "err", text }

  // Referral statistics drill-down.
  const [statsFor, setStatsFor] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsSearch, setStatsSearch] = useState("");

  const [busyId, setBusyId] = useState("");

  // ── LOAD EVERYTHING ──
  const load = useCallback(async (showSpinner) => {
    if (showSpinner) setLoading(true);
    try {
      const [ov, ex, fr] = await Promise.all([
        salesAdmin.overview(),
        salesAdmin.listExecutives(),
        salesAdmin.fraudReports(),
      ]);
      setOverview(ov);
      setExecutives(ex.executives || []);
      setFraud(fr.reports || []);
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Failed to load Sales Executive data"));
    } finally {
      setLoading(false);
    }
  }, []);

  // The incentive queue reloads on its own whenever the status filter changes.
  const loadIncentives = useCallback(async (status) => {
    try {
      const res = await salesAdmin.listIncentives(
        status === "all" ? { limit: 50 } : { status, limit: 50 }
      );
      setIncentives(res.items || []);
    } catch (err) {
      setError(errorMessage(err, "Failed to load incentives"));
    }
  }, []);

  useEffect(() => {
    load(true);
  }, [load]);

  useEffect(() => {
    loadIncentives(incentiveFilter);
  }, [incentiveFilter, loadIncentives]);

  const refreshAll = () => {
    load(false);
    loadIncentives(incentiveFilter);
  };

  // ── CREATE ──
  const createExecutive = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setFormMsg({ type: "err", text: "Both name and email are required." });
      return;
    }
    setCreating(true);
    setFormMsg(null);
    try {
      const res = await salesAdmin.createExecutive({
        name: name.trim(),
        email: email.trim().toLowerCase(),
      });
      setFormMsg({
        type: "ok",
        text: `${res.message} · Referral code: ${res.executive.referralCode}`,
      });
      setName("");
      setEmail("");
      refreshAll();
    } catch (err) {
      setFormMsg({ type: "err", text: errorMessage(err, "Could not create Sales Executive") });
    } finally {
      setCreating(false);
    }
  };

  // ── EXECUTIVE CONTROLS ──
  const changeStatus = async (exec, status) => {
    setBusyId(exec.id);
    try {
      await salesAdmin.setStatus(exec.id, status);
      refreshAll();
    } catch (err) {
      setError(errorMessage(err, "Could not update status"));
    } finally {
      setBusyId("");
    }
  };

  const removeExecutive = async (exec) => {
    if (
      !window.confirm(
        `Remove the Sales Executive role from ${exec.name}?\n\n` +
          "Their referrals and incentive history are kept — only the role and " +
          "dashboard access are revoked."
      )
    ) {
      return;
    }
    setBusyId(exec.id);
    try {
      await salesAdmin.removeExecutive(exec.id);
      refreshAll();
    } catch (err) {
      setError(errorMessage(err, "Could not remove Sales Executive"));
    } finally {
      setBusyId("");
    }
  };

  // ── INCENTIVE REVIEW ──
  const reviewIncentive = async (row, action) => {
    let reason = "";
    if (action === "reject") {
      reason = window.prompt("Reason for rejecting this incentive?") || "";
      if (!reason.trim()) return; // cancelled — nothing happens
    }
    setBusyId(row._id);
    try {
      await salesAdmin.reviewIncentive(row._id, action, reason);
      refreshAll();
    } catch (err) {
      setError(errorMessage(err, "Could not update incentive"));
    } finally {
      setBusyId("");
    }
  };

  // ── REFERRAL STATISTICS ──
  // The funnel table shows the most recent referrals; `search` is how an admin
  // audits one specific referred account once the list outgrows a single page.
  const loadStats = useCallback(async (execId, execName, search) => {
    setStatsLoading(true);
    try {
      const res = await salesAdmin.referralStats(
        execId,
        search ? { search } : undefined
      );
      setStatsFor(res);
    } catch (err) {
      setError(errorMessage(err, "Could not load referral statistics"));
      setStatsFor(null);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const openStats = (exec) => {
    setStatsSearch("");
    setStatsFor({ id: exec.id, name: exec.name });
    loadStats(exec.id, exec.name);
  };

  const searchStats = (e) => {
    e.preventDefault();
    const id = statsFor?.executive?.id || statsFor?.id;
    if (id) loadStats(id, statsFor?.executive?.name, statsSearch.trim());
  };

  const body = (
    <>
      {/* ══ HEADER ══ */}
      <div className="asx-header">
        <div>
          <h2 className="asx-title">🧑‍💼 Sales Executive Management</h2>
          <p className="asx-sub">
            Assign the role to existing accounts, review incentives and monitor
            referral quality.
          </p>
        </div>
        <button className="asx-btn" onClick={refreshAll} disabled={loading}>
          {loading ? "Refreshing…" : "↻ Refresh"}
        </button>
      </div>

      {error && <div className="asx-error">⚠️ {error}</div>}

      {/* ══ OVERVIEW (Task 7) ══ */}
      <div className="asx-cards">
        {[
          { icon: "🧑‍💼", num: overview?.totalSalesExecutives ?? 0, label: "Total Sales Executives" },
          { icon: "👥", num: overview?.totalSignups ?? 0, label: "Total Signups" },
          { icon: "🏆", num: overview?.totalValidUsers ?? 0, label: "Total Valid Users" },
          { icon: "🆕", num: overview?.todaySignups ?? 0, label: "Today's Signups" },
          { icon: "✅", num: overview?.todayValidUsers ?? 0, label: "Today's Valid Users" },
          { icon: "⏳", num: rupees(overview?.pendingIncentives), label: "Pending Incentives" },
          { icon: "✔️", num: rupees(overview?.approvedIncentives), label: "Approved Incentives" },
          { icon: "✖️", num: rupees(overview?.rejectedIncentives), label: "Rejected Incentives" },
          { icon: "🚨", num: overview?.fraudReports ?? 0, label: "Fraud Reports" },
        ].map((c) => (
          <div key={c.label} className="asx-card">
            <span className="asx-card-icon">{c.icon}</span>
            <span className="asx-card-num">{c.num}</span>
            <span className="asx-card-label">{c.label}</span>
          </div>
        ))}
      </div>

      {/* ══ CREATE (Task 1) ══ */}
      <div className="asx-block">
        <h3 className="asx-block-title">➕ Create Sales Executive</h3>
        <p className="asx-hint">
          The Sales Executive must already have an account on the app. Enter
          their registered name and email — the existing account is upgraded, no
          new user is created.
        </p>
        <form className="asx-form" onSubmit={createExecutive}>
          <input
            className="asx-input"
            placeholder="Registered name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            className="asx-input"
            type="email"
            placeholder="Registered email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <button className="asx-btn asx-btn-primary" disabled={creating}>
            {creating ? "Creating…" : "Create Account"}
          </button>
        </form>
        {formMsg && (
          <div className={formMsg.type === "ok" ? "asx-ok" : "asx-error"}>
            {formMsg.type === "ok" ? "✅ " : "⚠️ "}
            {formMsg.text}
          </div>
        )}
      </div>

      {/* ══ EXECUTIVES (Task 8) ══ */}
      <div className="asx-block">
        <h3 className="asx-block-title">📋 Sales Executives ({executives.length})</h3>
        <div className="asx-table-wrap">
          <table className="asx-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Code</th>
                <th>Signups</th>
                <th>Valid</th>
                <th>Today</th>
                <th>Pending ₹</th>
                <th>Paid ₹</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {executives.length === 0 ? (
                <tr>
                  <td colSpan={10} className="asx-empty">
                    No Sales Executives yet. Create one above.
                  </td>
                </tr>
              ) : (
                executives.map((ex) => (
                  <tr key={ex.id}>
                    <td className="asx-strong">{ex.name}</td>
                    <td className="asx-muted">{ex.email}</td>
                    <td>
                      <span className="asx-code">{ex.referralCode || "—"}</span>
                    </td>
                    <td>{ex.totalSignups}</td>
                    <td>{ex.totalValidUsers}</td>
                    <td className="asx-muted">
                      {ex.todaySignups} / {ex.todayValidUsers}
                    </td>
                    <td>{rupees(ex.pendingIncentive)}</td>
                    <td>{rupees(ex.paidIncentive)}</td>
                    <td>
                      <span className={`asx-badge asx-badge-${ex.status}`}>{ex.status}</span>
                    </td>
                    <td>
                      <div className="asx-actions">
                        <button
                          className="asx-btn asx-btn-sm"
                          onClick={() => openStats(ex)}
                          disabled={busyId === ex.id}
                        >
                          Referrals
                        </button>
                        {ex.status === "active" ? (
                          <button
                            className="asx-btn asx-btn-sm asx-btn-warn"
                            onClick={() => changeStatus(ex, "suspended")}
                            disabled={busyId === ex.id}
                          >
                            Suspend
                          </button>
                        ) : (
                          <button
                            className="asx-btn asx-btn-sm asx-btn-ok"
                            onClick={() => changeStatus(ex, "active")}
                            disabled={busyId === ex.id}
                          >
                            Activate
                          </button>
                        )}
                        <button
                          className="asx-btn asx-btn-sm asx-btn-danger"
                          onClick={() => removeExecutive(ex)}
                          disabled={busyId === ex.id}
                        >
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ══ INCENTIVES (Task 8) ══ */}
      <div className="asx-block">
        <div className="asx-block-head">
          <h3 className="asx-block-title">💰 Incentives</h3>
          <div className="asx-filters">
            {["pending", "approved", "paid", "rejected", "all"].map((s) => (
              <button
                key={s}
                className={`asx-chip ${incentiveFilter === s ? "asx-chip-active" : ""}`}
                onClick={() => setIncentiveFilter(s)}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="asx-table-wrap">
          <table className="asx-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Sales Executive</th>
                <th>Valid Users</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {incentives.length === 0 ? (
                <tr>
                  <td colSpan={6} className="asx-empty">
                    No {incentiveFilter === "all" ? "" : incentiveFilter} incentives.
                  </td>
                </tr>
              ) : (
                incentives.map((row) => (
                  <tr key={row._id}>
                    <td>{fmtDay(row.day)}</td>
                    <td className="asx-strong">
                      {row.salesExecutive?.name || "—"}
                      <span className="asx-muted asx-block-email">
                        {row.salesExecutive?.email || ""}
                      </span>
                    </td>
                    <td>{row.validUsers}</td>
                    <td className="asx-strong">{rupees(row.amount)}</td>
                    <td>
                      <span className={`asx-badge asx-badge-${row.status}`}>{row.status}</span>
                      {row.reason && <div className="asx-reason">“{row.reason}”</div>}
                    </td>
                    <td>
                      <div className="asx-actions">
                        {row.status === "pending" && (
                          <>
                            <button
                              className="asx-btn asx-btn-sm asx-btn-ok"
                              onClick={() => reviewIncentive(row, "approve")}
                              disabled={busyId === row._id}
                            >
                              Approve
                            </button>
                            <button
                              className="asx-btn asx-btn-sm asx-btn-danger"
                              onClick={() => reviewIncentive(row, "reject")}
                              disabled={busyId === row._id}
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {row.status === "approved" && (
                          <button
                            className="asx-btn asx-btn-sm asx-btn-primary"
                            onClick={() => reviewIncentive(row, "pay")}
                            disabled={busyId === row._id}
                          >
                            Mark Paid
                          </button>
                        )}
                        {(row.status === "paid" || row.status === "rejected") && (
                          <span className="asx-muted">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ══ FRAUD REPORTS (Task 8) ══ */}
      <div className="asx-block">
        <h3 className="asx-block-title">🚨 Fraud Reports ({fraud.length})</h3>
        <p className="asx-hint">
          Derived automatically from referral and login-session data — no manual
          reporting needed.
        </p>
        {fraud.length === 0 ? (
          <div className="asx-empty asx-empty-box">
            No suspicious referral activity detected.
          </div>
        ) : (
          <div className="asx-fraud-list">
            {fraud.map((f, i) => (
              <div key={`${f.type}-${f.executive.id}-${i}`} className={`asx-fraud asx-fraud-${f.severity}`}>
                <div className="asx-fraud-top">
                  <span className="asx-fraud-type">{f.type.replace(/_/g, " ")}</span>
                  <span className={`asx-badge asx-badge-${f.severity === "high" ? "rejected" : "pending"}`}>
                    {f.severity}
                  </span>
                </div>
                <div className="asx-strong">{f.executive.name}</div>
                <div className="asx-muted">{f.executive.email}</div>
                <p className="asx-fraud-detail">{f.detail}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ══ POINT 10 SECURITY SYSTEM + POINT 11 T&C (additive panel) ══ */}
      <AdminSecurityPanel />

      {/* ══ REFERRAL STATISTICS MODAL ══ */}
      {statsFor && (
        <div className="asx-modal-backdrop" onClick={() => setStatsFor(null)}>
          <div className="asx-modal" onClick={(e) => e.stopPropagation()}>
            <div className="asx-modal-head">
              <h3>🔗 Referral Statistics — {statsFor.executive?.name || statsFor.name}</h3>
              <button className="asx-btn asx-btn-sm" onClick={() => setStatsFor(null)}>
                Close
              </button>
            </div>

            {statsLoading ? (
              <div className="asx-empty">Loading…</div>
            ) : (
              <>
                <div className="asx-modal-stats">
                  <span>
                    Signups: <b>{statsFor.totalSignups ?? 0}</b>
                  </span>
                  <span>
                    Valid: <b>{statsFor.totalValidUsers ?? 0}</b>
                  </span>
                  <span>
                    Not yet valid: <b>{statsFor.pendingValidation ?? 0}</b>
                  </span>
                  <span>
                    Code: <b className="asx-code">{statsFor.executive?.referralCode || "—"}</b>
                  </span>
                </div>

                <form className="asx-form" onSubmit={searchStats}>
                  <input
                    className="asx-input"
                    placeholder="Search referred users by name or email…"
                    value={statsSearch}
                    onChange={(e) => setStatsSearch(e.target.value)}
                  />
                  <button className="asx-btn asx-btn-sm asx-btn-primary">Search</button>
                  {statsFor.search ? (
                    <button
                      type="button"
                      className="asx-btn asx-btn-sm"
                      onClick={() => {
                        setStatsSearch("");
                        loadStats(statsFor.executive.id, statsFor.executive.name);
                      }}
                    >
                      Clear
                    </button>
                  ) : null}
                </form>

                <p className="asx-hint">
                  Showing {statsFor.shown ?? 0} of {statsFor.totalSignups ?? 0} referred
                  users{statsFor.search ? ` matching “${statsFor.search}”` : " (most recent first)"}.
                </p>

                <div className="asx-table-wrap">
                  <table className="asx-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Registered</th>
                        <th>Logged in</th>
                        <th>Published blog</th>
                        <th>Valid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(statsFor.referredUsers || []).length === 0 ? (
                        <tr>
                          <td colSpan={5} className="asx-empty">
                            No referred users yet.
                          </td>
                        </tr>
                      ) : (
                        statsFor.referredUsers.map((u) => (
                          <tr key={u.id}>
                            <td className="asx-strong">
                              {u.name}
                              <span className="asx-muted asx-block-email">{u.email}</span>
                            </td>
                            <td className="asx-muted">{fmtDate(u.registeredAt)}</td>
                            <td>{u.hasLoggedIn ? "✅" : "—"}</td>
                            <td>{u.hasPublishedBlog ? "✅" : "—"}</td>
                            <td>
                              <span
                                className={`asx-badge asx-badge-${u.isValid ? "approved" : "pending"}`}
                              >
                                {u.isValid ? "valid" : "pending"}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );

  // Embedded inside the Admin Dashboard: section only, no page chrome.
  if (embedded) {
    return <div className="asx-embedded">{body}</div>;
  }

  return (
    <div className="asx-root">
      <Navbar />
      <div className="asx-page">{body}</div>
    </div>
  );
}
